/**
 * Start an Investigator run from a capability card.
 *
 * TWO MODES, and the difference is a promise to the user:
 *
 *   PINNED (`member_drug_ids` present) — the user is looking at a result set,
 *     so the card promised a verdict for every one of THOSE products. Sending
 *     `drug_ids` is what keeps that true: without it the backend discovers its
 *     own cohort and would silently answer about a different set. The backend
 *     also forces `debug=true` for a pinned cohort so NON-MATCHES are
 *     persisted — without those rows, "187 inspected, 21 matched" is a
 *     completeness claim the data cannot support.
 *
 *   DISCOVERY (no members) — Investigator's NATIVE mode, and the only one
 *     available off a result set. `research_service.py`: "omit it to discover
 *     a cohort". The card promised a corpus search, not per-member coverage,
 *     so there is nothing to pin and nothing overclaimed.
 *
 * Everything else is left at the route's defaults. The user sets Document
 * Type / Region / Application Type on the Investigator surface itself when
 * they drive; CEPI does not guess them here.
 */
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from './impersonationHeaders';
import type { CapabilityScopeDraft } from '../types/qa';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export interface StartCohortJobResult {
  jobId?: string;
  error?: string;
}

/**
 * The most products one Investigator run may verdict.
 *
 * Enforced TWICE on the route and this mirrors both:
 *
 *     research_routes.py:63   max_results: int = Field(..., ge=1, le=500)
 *     research_routes.py:84   drug_ids: ... max_length=500
 *
 * so a larger pin is refused with a 422 before the job is created — nothing
 * truncates and no member silently goes unevaluated. Checking here as well is
 * not belt-and-braces: without it the user reads a raw pydantic validation
 * string in the workspace, which names a field they have never heard of and
 * says nothing about what to do.
 *
 * Measured cost for context (prod, 2026-09-23): ~5s per product, so a full
 * 500-product run is roughly 44 minutes.
 */
export const MAX_PINNED_PRODUCTS = 500;

/** Discovery-run match cap sent to the route; also quoted in the tab announcement. */
export const DISCOVERY_MAX_RESULTS = 25;

export async function startCohortJob(
  scope: CapabilityScopeDraft,
): Promise<StartCohortJobResult> {
  const pinned = scope.member_drug_ids?.length
    ? scope.member_drug_ids
    : null;
  // A pinned scope that lost its ids is the one case still worth refusing:
  // the card promised "every one of these N", and running discovery instead
  // would quietly answer about a different set under that promise.
  if (scope.coverage_mode === 'complete' && !pinned) {
    return { error: 'This scope has no products.' };
  }
  if (!scope.criterion?.trim()) {
    return { error: 'This scope has no criterion.' };
  }
  // Said in products, because that is the thing the user chose and can change.
  if (pinned && pinned.length > MAX_PINNED_PRODUCTS) {
    return {
      error: `${pinned.length} products is more than Investigator can `
        + `verdict in one run. Narrow the set to ${MAX_PINNED_PRODUCTS} or `
        + `fewer, or use Agentic reporting for a scoped plan.`,
    };
  }
  try {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken(false) : null;
    const res = await fetch(`${API_BASE_URL}/v2/research`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...getImpersonationHeaders(),
      },
      body: JSON.stringify({
        query: scope.criterion,
        // Omitted entirely on a discovery run — the route treats null and
        // absent differently ("pinned drug_ids is empty — omit it to discover
        // a cohort"), and sending an empty list is a 422.
        ...(pinned ? { drug_ids: pinned } : {}),
        // Pinned: only bounds the per-member cap applied elsewhere, so it must
        // not be smaller than the cohort. Discovery: the route's own default,
        // which is also what the Investigator surface starts at.
        max_results: pinned ? Math.max(pinned.length, 25) : DISCOVERY_MAX_RESULTS,
        corpus: 'labels',
      }),
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        if (typeof body?.detail === 'string') detail = body.detail;
      } catch { /* non-JSON error body */ }
      return { error: detail };
    }
    const job = await res.json();
    return typeof job?.id === 'string'
      ? { jobId: job.id }
      : { error: 'The server did not return a job id.' };
  } catch {
    return { error: 'Could not reach the server.' };
  }
}
